/**
 * The TanStack Query layer — every read and write the dashboard performs.
 *
 * Two things matter here more than anything else:
 *
 *  1. **Keys are hierarchical.** `['af','inquiries','detail',id]` sits under
 *     `['af','inquiries']`, so invalidation can be surgical instead of nuclear.
 *  2. **Mutations are optimistic and complete.** A stage change repaints the
 *     row in every open list, the card on the board and the detail pane in the
 *     same frame, and every one of those rolls back together if the server says
 *     no. Half-optimistic is worse than not optimistic — it makes the UI lie.
 */

import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import {
  ApiError,
  api,
  type AnalyticsQuery,
  type BoardQuery,
  type BoardResponse,
  type BreakdownQuery,
  type BugQuery,
  type CompanyInput,
  type InquiryPatch,
  type LeaderboardQuery,
  type Message,
  type MessageInput,
  type MessagePatch,
  type NoteInput,
  type SavedViewInput,
  type SearchResponse,
  type TaskGroupBy,
  type TaskInput,
  type TimeseriesQuery,
} from '@/lib/api';
import {
  BOARD_PAGE_SIZE,
  PAGE_SIZE,
  livePipelineVersion,
  setLivePipeline,
  subscribeLivePipeline,
} from '@/lib/constants';
import { sanitiseFilterSet } from '@/lib/filters';
import { toast } from '@/components/ui';
import type {
  Activity,
  Attachment,
  BugPatch,
  BulkRequest,
  Company,
  Contact,
  FilterSet,
  ID,
  Inquiry,
  ListQuery,
  Note,
  Paginated,
  SavedView,
  SessionResponse,
  SortSpec,
  Tag,
  Task,
  User,
  WebhookInput,
  WebhookPatch,
} from '@shared/types';

/* ────────────────────────────────────────────────────────────────────────────
   Key factory
   ──────────────────────────────────────────────────────────────────────────── */

const ROOT = 'af';

/**
 * The part of a `ListQuery` that identifies a result set. Condition ids are
 * regenerated on every edit, so they are replaced with deterministic ones and
 * the conditions are sorted — otherwise typing in the filter builder would
 * churn cache entries that represent the very same query.
 */
export interface ListKeyPart {
  q: string;
  filters?: FilterSet;
  sort?: SortSpec[];
  limit: number;
  viewId?: ID;
}

function normaliseConditions(set: FilterSet): FilterSet {
  return {
    combinator: set.combinator,
    conditions: set.conditions
      .map((c) => ({
        ...c,
        id: `${c.field}~${c.operator}`,
        // Ticking "Review" then "Qualifying" is the same query as the reverse —
        // it must not open a second cache entry. `between` is order-significant.
        value:
          c.operator === 'between' || !Array.isArray(c.value)
            ? c.value
            : [...c.value].sort((x, y) => String(x).localeCompare(String(y))),
      }))
      .sort((a, b) =>
        `${a.id}${JSON.stringify(a.value)}`.localeCompare(`${b.id}${JSON.stringify(b.value)}`),
      ),
  };
}

export function stableFilterSet(set: FilterSet | undefined): FilterSet | undefined {
  const clean = sanitiseFilterSet(set);
  if (clean.conditions.length === 0) return undefined;
  return normaliseConditions(clean);
}

/**
 * The cache identity of a filter set.
 *
 * `stableFilterSet` drops any condition whose field is not in the *inquiry*
 * filter catalogue, which is right for the filter builder and wrong for a cache
 * key: two genuinely different task queries (`dueAt <= now` and `dueAt >= now`,
 * say) would sanitise to nothing, collapse onto one key, and the second hook
 * would silently render the first one's rows. Whenever sanitising *drops*
 * anything — the whole set or one condition out of three — key off the raw
 * conditions instead. A partial drop is the same bug in slower motion: the
 * contacts list filters on `title` and `inquiries`, neither of which is in the
 * inquiry catalogue, so "Region EU + Director" and "Region EU + Manager" would
 * both have keyed on the region alone.
 */
function filterKeyPart(set: FilterSet | undefined): FilterSet | undefined {
  if (!set || set.conditions.length === 0) return undefined;
  const clean = stableFilterSet(set);
  if (clean && clean.conditions.length === set.conditions.length) return clean;
  return normaliseConditions(set);
}

export function listKeyPart(query: ListQuery = {}, defaultLimit = PAGE_SIZE): ListKeyPart {
  const part: ListKeyPart = {
    q: query.q?.trim() ?? '',
    limit: query.limit ?? defaultLimit,
  };
  const filters = filterKeyPart(query.filters);
  if (filters) part.filters = filters;
  if (query.sort && query.sort.length > 0) part.sort = query.sort;
  if (query.viewId) part.viewId = query.viewId;
  return part;
}

export const queryKeys = {
  all: [ROOT] as const,

  session: () => [ROOT, 'session'] as const,

  inquiries: {
    all: () => [ROOT, 'inquiries'] as const,
    lists: () => [ROOT, 'inquiries', 'list'] as const,
    list: (query: ListQuery = {}) => [ROOT, 'inquiries', 'list', listKeyPart(query)] as const,
    boards: () => [ROOT, 'inquiries', 'board'] as const,
    board: (query: BoardQuery = {}) =>
      [
        ROOT,
        'inquiries',
        'board',
        listKeyPart(query, BOARD_PAGE_SIZE),
        query.perColumn ?? BOARD_PAGE_SIZE,
      ] as const,
    details: () => [ROOT, 'inquiries', 'detail'] as const,
    detail: (id: ID) => [ROOT, 'inquiries', 'detail', id] as const,
    activities: (id: ID) => [ROOT, 'inquiries', 'detail', id, 'activities'] as const,
    notes: (id: ID) => [ROOT, 'inquiries', 'detail', id, 'notes'] as const,
    tasks: (id: ID) => [ROOT, 'inquiries', 'detail', id, 'tasks'] as const,
  },

  contacts: {
    all: () => [ROOT, 'contacts'] as const,
    lists: () => [ROOT, 'contacts', 'list'] as const,
    list: (query: ListQuery = {}) => [ROOT, 'contacts', 'list', listKeyPart(query)] as const,
    detail: (id: ID) => [ROOT, 'contacts', 'detail', id] as const,
  },

  companies: {
    all: () => [ROOT, 'companies'] as const,
    lists: () => [ROOT, 'companies', 'list'] as const,
    list: (query: ListQuery = {}) => [ROOT, 'companies', 'list', listKeyPart(query)] as const,
    detail: (id: ID) => [ROOT, 'companies', 'detail', id] as const,
    facets: () => [ROOT, 'companies', 'facets'] as const,
  },

  tasks: {
    all: () => [ROOT, 'tasks'] as const,
    lists: () => [ROOT, 'tasks', 'list'] as const,
    list: (query: ListQuery = {}) => [ROOT, 'tasks', 'list', listKeyPart(query)] as const,
    groupCounts: (groupBy: string, query: ListQuery = {}) =>
      [ROOT, 'tasks', 'group-counts', groupBy, listKeyPart(query)] as const,
  },

  tags: () => [ROOT, 'tags'] as const,
  webhooks: () => [ROOT, 'webhooks'] as const,
  users: () => [ROOT, 'users'] as const,
  stages: () => [ROOT, 'stages'] as const,
  views: () => [ROOT, 'saved-views'] as const,
  viewCounts: () => [ROOT, 'saved-views', 'counts'] as const,

  search: (term: string) => [ROOT, 'search', term.trim().toLowerCase()] as const,

  analytics: {
    all: () => [ROOT, 'analytics'] as const,
    overview: (query: AnalyticsQuery = {}) =>
      [ROOT, 'analytics', 'overview', analyticsKeyPart(query)] as const,
    timeseries: (query: TimeseriesQuery = {}) =>
      [
        ROOT,
        'analytics',
        'timeseries',
        analyticsKeyPart(query),
        query.granularity ?? 'auto',
        query.groupBy ?? 'none',
        query.metric ?? 'count',
      ] as const,
    breakdown: (query: BreakdownQuery) =>
      [ROOT, 'analytics', 'breakdown', analyticsKeyPart(query), query.dimension, query.limit ?? 10] as const,
    funnel: (query: AnalyticsQuery = {}) =>
      [ROOT, 'analytics', 'funnel', analyticsKeyPart(query)] as const,
    responseTimes: (query: AnalyticsQuery = {}) =>
      [ROOT, 'analytics', 'response-times', analyticsKeyPart(query)] as const,
    leaderboard: (query: LeaderboardQuery = {}) =>
      [ROOT, 'analytics', 'leaderboard', analyticsKeyPart(query), query.limit ?? 12] as const,
  },

  audit: (query: object = {}) => [ROOT, 'audit', query] as const,

  bugs: () => [ROOT, 'bugs'] as const,
  bugSummary: () => [ROOT, 'bugs', 'summary'] as const,
  bugList: (query: object = {}) => [ROOT, 'bugs', 'list', query] as const,
  bugDetail: (id: number) => [ROOT, 'bugs', 'detail', id] as const,
  bugUnverified: () => [ROOT, 'bugs', 'unverified-count'] as const,
} as const;

function analyticsKeyPart(query: AnalyticsQuery): {
  range: string;
  from?: string;
  to?: string;
  filters?: FilterSet;
} {
  const part: { range: string; from?: string; to?: string; filters?: FilterSet } = {
    range: query.range ?? '30d',
  };
  if (query.from) part.from = query.from;
  if (query.to) part.to = query.to;
  const filters = stableFilterSet(query.filters);
  if (filters) part.filters = filters;
  return part;
}

/* ────────────────────────────────────────────────────────────────────────────
   Client
   ──────────────────────────────────────────────────────────────────────────── */

/** 4xx means the request was wrong; retrying just delays the error state. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (ApiError.is(error) && error.isClientError && error.status !== 429) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      },
      mutations: { retry: false },
    },
  });
}

/** The app's single client — also what `@/lib/realtime` writes into. */
export const queryClient = createQueryClient();

function reportError(error: unknown, fallback: string): void {
  const message = ApiError.is(error) ? error.message : fallback;
  const requestId = ApiError.is(error) ? error.requestId : '';
  toast.error(message, requestId ? { description: `Request ${requestId}` } : undefined);
}

/* ────────────────────────────────────────────────────────────────────────────
   Shared selectors
   ──────────────────────────────────────────────────────────────────────────── */

export interface InfiniteRows<T> {
  rows: T[];
  /** Total matching the filters server-side, not `rows.length`. */
  total: number;
  loadedCount: number;
}

type Pages<T> = InfiniteData<Paginated<T>>;

function flatten<T>(data: Pages<T>): InfiniteRows<T> {
  const rows = data.pages.flatMap((page) => page.items);
  return {
    rows,
    total: data.pages[data.pages.length - 1]?.total ?? rows.length,
    loadedCount: rows.length,
  };
}

const selectInquiryRows = (data: Pages<Inquiry>): InfiniteRows<Inquiry> => flatten(data);
const selectContactRows = (data: Pages<Contact>): InfiniteRows<Contact> => flatten(data);
const selectCompanyRows = (data: Pages<Company>): InfiniteRows<Company> => flatten(data);
const selectTaskRows = (data: Pages<Task>): InfiniteRows<Task> => flatten(data);

/* ────────────────────────────────────────────────────────────────────────────
   Session
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * "Has anyone ever signed in in this browser?"
 *
 * The session itself lives in an httpOnly cookie that JS cannot read, so the
 * only way to know whether we are signed in is to ask the server. On a *guarded*
 * route that probe is always worth making. On `/login` it usually is not: a
 * first-time visitor has no cookie, so the probe is a guaranteed 401 — a wasted
 * round trip in front of the form and a red line in everyone's console.
 *
 * This flag is the cheap local hint that makes the probe worth making. It is
 * only ever a hint: wrong-and-true costs one 401, wrong-and-false costs an
 * already-signed-in user one extra sign-in. Neither can lock anyone out.
 */
const SIGNED_IN_HINT_KEY = 'af.session.seen';

export function hasSignedInBefore(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT_KEY) === '1';
  } catch {
    // Private mode / storage disabled — probe rather than lock anyone out.
    return true;
  }
}

function setSignedInHint(value: boolean): void {
  try {
    if (value) window.localStorage.setItem(SIGNED_IN_HINT_KEY, '1');
    else window.localStorage.removeItem(SIGNED_IN_HINT_KEY);
  } catch {
    /* storage unavailable — the hint is optional by design */
  }
}

export interface SessionHookOptions {
  /** `/login` passes `false` when there is no reason to expect a session. */
  enabled?: boolean;
}

/** A 401 is an answer; anything else is the network or the server having a moment. */
export function isUnauthorized(error: unknown): boolean {
  return ApiError.is(error) && error.status === 401;
}

export function useSession(options: SessionHookOptions = {}) {
  const query = useQuery({
    queryKey: queryKeys.session(),
    queryFn: ({ signal }) => api.auth.me(signal),
    // Never retry what the *client* got wrong — a 401 will not change its mind
    // and a 429 is the server saying the last thing to do is ask again — but
    // never let one 500 or one dropped connection sign a working session out
    // either. The budget on those is five attempts over ~7s rather than two
    // over ~1.2s: an API that is restarting (or a proxy answering 500 for a
    // socket it could not open) is unreachable for seconds, not milliseconds,
    // and every route in the app is gated on this one probe — losing it
    // replaces the whole screen with "We could not confirm your session". A
    // real outage still lands there; it just has to still be one 7s later.
    retry: (attempt, error) =>
      !(ApiError.is(error) && error.isClientError) && attempt < 5,
    retryDelay: (attempt) => Math.min(400 * 2 ** attempt, 2_000),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    enabled: options.enabled ?? true,
  });

  // Keep the hint honest: a live session sets it, a *rejected* probe clears it.
  // A 500 clears nothing: the cookie is probably still good.
  useEffect(() => {
    if (query.data) setSignedInHint(true);
    else if (query.isError && isUnauthorized(query.error)) setSignedInHint(false);
  }, [query.data, query.isError, query.error]);

  /**
   * A 5xx on a session *refresh* must not evict a signed-in user from the screen
   * they are working on. While a known-good session is still in cache the hook
   * reports "reconnecting" rather than "error", so the route guard keeps the
   * screen mounted and the header can say so quietly. Only a 401 — or a failure
   * with no session behind it — is allowed to surface as an error and bounce to
   * `/login`.
   */
  const reconnecting =
    query.isError && !isUnauthorized(query.error) && query.data !== undefined;

  return { ...query, isError: query.isError && !reconnecting, reconnecting };
}

/** Convenience for permission checks: `useHasPermission('inquiries.delete')`. */
export function useHasPermission(permission: string): boolean {
  const { data } = useSession();
  return data?.permissions.includes(permission) ?? false;
}

export function useLogin(): UseMutationResult<SessionResponse, unknown, { email: string; password: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api.auth.login(body),
    onSuccess: (session) => {
      setSignedInHint(true);
      qc.setQueryData(queryKeys.session(), session);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      setSignedInHint(false);
      qc.clear();
    },
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Inquiries — reads
   ──────────────────────────────────────────────────────────────────────────── */

export interface ListHookOptions {
  enabled?: boolean;
}

export function useInquiries(query: ListQuery = {}, options: ListHookOptions = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.inquiries.list(query),
    queryFn: ({ pageParam, signal }) =>
      api.inquiries.list({ ...query, limit: query.limit ?? PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: selectInquiryRows,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useInquiry(id: ID | null | undefined, options: ListHookOptions = {}) {
  const qc = useQueryClient();
  return useQuery<Inquiry>({
    queryKey: queryKeys.inquiries.detail(id ?? 'none'),
    queryFn: ({ signal }) => api.inquiries.get(id as ID, signal),
    enabled: Boolean(id) && (options.enabled ?? true),
    /** Paint the row we already have while the hydrated record loads. */
    placeholderData: () => (id ? findInquiryInLists(qc, id) : undefined),
  });
}

export function useBoard(query: BoardQuery = {}, options: ListHookOptions = {}) {
  return useQuery({
    queryKey: queryKeys.inquiries.board(query),
    queryFn: ({ signal }) =>
      api.inquiries.board({ ...query, perColumn: query.perColumn ?? BOARD_PAGE_SIZE }, signal),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useInquiryActivities(id: ID | null | undefined) {
  return useQuery({
    queryKey: queryKeys.inquiries.activities(id ?? 'none'),
    queryFn: ({ signal }) => api.inquiries.activities(id as ID, undefined, signal),
    enabled: Boolean(id),
  });
}

export function useInquiryNotes(id: ID | null | undefined) {
  return useQuery({
    queryKey: queryKeys.inquiries.notes(id ?? 'none'),
    queryFn: ({ signal }) => api.inquiries.notes(id as ID, signal),
    enabled: Boolean(id),
  });
}

export function useInquiryTasks(id: ID | null | undefined) {
  return useQuery({
    queryKey: queryKeys.inquiries.tasks(id ?? 'none'),
    queryFn: ({ signal }) => api.inquiries.tasks(id as ID, signal),
    enabled: Boolean(id),
  });
}

/** Hover a row → the detail pane is already warm when it is clicked. */
export function usePrefetchInquiry(): (id: ID) => void {
  const qc = useQueryClient();
  return (id: ID) => {
    void qc.prefetchQuery({
      queryKey: queryKeys.inquiries.detail(id),
      queryFn: ({ signal }) => api.inquiries.get(id, signal),
      staleTime: 30_000,
    });
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Other entities
   ──────────────────────────────────────────────────────────────────────────── */

export function useContacts(query: ListQuery = {}, options: ListHookOptions = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.contacts.list(query),
    queryFn: ({ pageParam, signal }) =>
      api.contacts.list({ ...query, limit: query.limit ?? PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: selectContactRows,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    /**
     * The same policy the account book already runs, for the same measured
     * reason. Under the shared default a hard 500 was retried twice behind a
     * 1s/2s backoff, so the surface held its skeleton for ~4.5s — six requests —
     * while impersonating a slow load, and every control gated on
     * `contacts.isError` (Export) stayed live throughout. An `ApiError` is the
     * server's own answer, carrying a `code` and a `requestId`: there is nothing
     * to wait for. Only an untyped failure — the connection dropped mid-flight —
     * is worth one quick retry, which puts the error panel up inside ~0.5s.
     */
    retry: (failureCount, error) => !ApiError.is(error) && failureCount < 1,
    retryDelay: () => 400,
  });
}

export function useContact(id: ID | null | undefined) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id ?? 'none'),
    queryFn: ({ signal }) => api.contacts.get(id as ID, signal),
    enabled: Boolean(id),
  });
}

export function useCompanies(query: ListQuery = {}, options: ListHookOptions = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.companies.list(query),
    queryFn: ({ pageParam, signal }) =>
      api.companies.list({ ...query, limit: query.limit ?? PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: selectCompanyRows,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    /**
     * A skeleton is a promise that data is coming. Under the shared policy a
     * typed 500 was retried twice behind a 1s/2s backoff, so the list shimmered
     * for 4.2 seconds after the server had already said no. An `ApiError` is the
     * server's own answer — it carries a `code` and a `requestId` — so there is
     * nothing to wait for: fail straight to the error state. Only an untyped
     * failure (the network dropped mid-flight) is worth one quick retry.
     */
    retry: (failureCount, error) => !ApiError.is(error) && failureCount < 1,
    retryDelay: () => 400,
  });
}

/** Creates an account and invalidates every companies list on success. */
export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompanyInput) => api.companies.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.all() });
    },
  });
}

/**
 * The account book's open vocabularies — today just `segment`.
 *
 * The list is the same for every reader and changes only when a company is
 * created or edited, so it is held for the session rather than refetched behind
 * every popover open. `useCreateCompany` invalidates `companies.all()`, which
 * covers this key too.
 */
export function useCompanyFacets(options: ListHookOptions = {}) {
  return useQuery({
    queryKey: queryKeys.companies.facets(),
    queryFn: ({ signal }) => api.companies.facets(signal),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

export function useCompany(id: ID | null | undefined) {
  return useQuery({
    queryKey: queryKeys.companies.detail(id ?? 'none'),
    queryFn: ({ signal }) => api.companies.get(id as ID, signal),
    enabled: Boolean(id),
  });
}

export function useTasks(query: ListQuery = {}, options: ListHookOptions = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.tasks.list(query),
    queryFn: ({ pageParam, signal }) =>
      api.tasks.list({ ...query, limit: query.limit ?? PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    select: selectTaskRows,
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/**
 * Every band's authoritative size for one grouping of the queue `useTasks` is
 * showing. Same query in, so the bands and the toolbar's count can never
 * describe two different sets.
 */
export function useTaskGroupCounts(
  groupBy: TaskGroupBy,
  query: ListQuery = {},
  options: ListHookOptions = {},
) {
  return useQuery({
    queryKey: queryKeys.tasks.groupCounts(groupBy, query),
    queryFn: ({ signal }) => api.tasks.groupCounts(groupBy, query, signal),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

/** Reference data — changes rarely, read constantly. */
export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags(),
    queryFn: ({ signal }) => api.tags.list(signal),
    staleTime: 5 * 60_000,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Webhooks

   Workspace configuration, so it comes from the API and every mutation
   invalidates the list. `refetchInterval` is the honest part: delivery happens
   off the request path, so an attempt lands in the table a second or two after
   the button that caused it — the panel polls while the tab is open rather than
   claiming a result it has not seen.
   ──────────────────────────────────────────────────────────────────────────── */

export function useWebhooks() {
  return useQuery({
    queryKey: queryKeys.webhooks(),
    queryFn: ({ signal }) => api.webhooks.list(signal),
    staleTime: 10_000,
    refetchInterval: 10_000,
  });
}

function useWebhookInvalidation() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: queryKeys.webhooks() });
  }, [qc]);
}

export function useCreateWebhook() {
  const invalidate = useWebhookInvalidation();
  return useMutation({
    mutationFn: (input: WebhookInput) => api.webhooks.create(input),
    onSuccess: invalidate,
  });
}

export function usePatchWebhook() {
  const invalidate = useWebhookInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: ID; patch: WebhookPatch }) => api.webhooks.patch(id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteWebhook() {
  const invalidate = useWebhookInvalidation();
  return useMutation({
    mutationFn: (id: ID) => api.webhooks.remove(id),
    onSuccess: invalidate,
  });
}

/** Send one test event, then look for the attempt it produced. */
export function useTestWebhook() {
  const invalidate = useWebhookInvalidation();
  return useMutation({
    mutationFn: (id: ID) => api.webhooks.test(id),
    onSuccess: () => window.setTimeout(invalidate, 1200),
  });
}

export function useRedeliverWebhook() {
  const invalidate = useWebhookInvalidation();
  return useMutation({
    mutationFn: (deliveryId: ID) => api.webhooks.redeliver(deliveryId),
    onSuccess: () => window.setTimeout(invalidate, 1200),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users(),
    queryFn: ({ signal }) => api.users.list(signal),
    staleTime: 10 * 60_000,
  });
}

/**
 * The pipeline, and the ONE publication of it.
 *
 * Every stage a surface paints — its label and its `--stage-*` step — resolves
 * through `stageMeta()`, which reads the registry this hook fills. Twelve
 * surfaces render a stage and only four of them fetch a pipeline; before the
 * registry existed the other eight silently showed the seeded defaults, so a
 * stage renamed in Settings was spelled one way on the board and another in the
 * command palette. `select` runs on every resolution, including the cached one,
 * so the registry is correct before the first component reads it.
 */
export function useStages() {
  return useQuery({
    queryKey: queryKeys.stages(),
    queryFn: ({ signal }) => api.pipeline.stages(signal),
    select: (stages) => {
      setLivePipeline(stages);
      return stages;
    },
    staleTime: 10 * 60_000,
  });
}

/**
 * Subscribe to the stage registry without owning the query.
 *
 * A surface that calls `stageMeta()` but does not itself fetch `/pipeline/stages`
 * has nothing to re-render it when the pipeline lands. This is that signal; the
 * shell holds one so the whole tree repaints once, and record screens that
 * render a stage outside the shell's own render hold their own.
 */
export function useLivePipeline(): number {
  return useSyncExternalStore(subscribeLivePipeline, livePipelineVersion, livePipelineVersion);
}

export function useSavedViews() {
  return useQuery({
    queryKey: queryKeys.views(),
    queryFn: ({ signal }) => api.views.list(signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * Every saved view's total in one request, under one cache key.
 *
 * The view rail and the sidebar both label their views with a count. Asking per
 * view cost ~17 round trips to paint the inbox and the counts popped in one at a
 * time, so the tab strip settled after the table did. Both surfaces read this.
 */
export function useViewCounts() {
  return useQuery({
    queryKey: queryKeys.viewCounts(),
    queryFn: ({ signal }) => api.views.counts(signal),
    staleTime: 60_000,
  });
}

export function useAuditLog(query: Parameters<typeof api.audit.list>[0] = {}) {
  return useQuery({
    queryKey: queryKeys.audit(query),
    queryFn: ({ signal }) => api.audit.list(query, signal),
    placeholderData: keepPreviousData,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Bug reports

   Admin-only reads. `useUnverifiedBugCount` is the exception: the server answers
   0 for everyone else rather than 403, so the rail can hold the hook
   unconditionally and never branch on role to decide whether to ask.
   ──────────────────────────────────────────────────────────────────────────── */

/** The rail badge. Counts OPEN AND UNCONFIRMED — the gap a fix can fall into. */
export function useUnverifiedBugCount() {
  return useQuery({
    queryKey: queryKeys.bugUnverified(),
    queryFn: ({ signal }) => api.bugs.unverifiedCount(signal),
    select: (data) => data.count,
    staleTime: 60_000,
  });
}

/**
 * The triage spine: every report, grouped by the page it was filed on.
 *
 * Polled, because reports arrive from other people rather than from anything
 * this tab did. 30s rather than the widget's own 12s — a CRM administrator is
 * not sitting on this screen waiting, and the query is a full table scan.
 * `refetchOnWindowFocus` does the rest: coming back to the tab is the moment
 * staleness is actually visible.
 */
export function useBugSummary() {
  return useQuery({
    queryKey: queryKeys.bugSummary(),
    queryFn: ({ signal }) => api.bugs.summary(signal),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useBugList(query: BugQuery = {}) {
  return useQuery({
    queryKey: queryKeys.bugList(query),
    queryFn: ({ signal }) => api.bugs.list(query, signal),
    select: (data) => data.bugs,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useBugReport(id: number | null) {
  return useQuery({
    queryKey: queryKeys.bugDetail(id ?? 0),
    queryFn: ({ signal }) => api.bugs.get(id as number, signal),
    enabled: id !== null,
  });
}

/** Every bug read at once. Status, verification and deletion all move counts. */
function useBugInvalidation() {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: queryKeys.bugs() });
  }, [client]);
}

/**
 * Status, triage notes and reclassification.
 *
 * Not optimistic. Every other mutation in this file is, because a stage change
 * has to repaint four surfaces in the same frame; here a single select on a
 * screen only administrators open is not worth the rollback path, and the
 * server's answer arrives in one round trip.
 */
export function useUpdateBug() {
  const invalidate = useBugInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: BugPatch }) => api.bugs.patch(id, patch),
    onSuccess: invalidate,
    onError: (error) => reportError(error, 'That report could not be updated.'),
  });
}

/**
 * The fix-confirmed flag. Separate from `status: 'resolved'` on purpose:
 * resolved means somebody shipped a change, verified means somebody looked. The
 * badge counts the difference, which is the only thing that catches a fix that
 * did not fix it.
 */
export function useVerifyBug() {
  const invalidate = useBugInvalidation();
  return useMutation({
    mutationFn: ({ id, verified }: { id: number; verified: boolean }) =>
      api.bugs.verify(id, verified),
    onSuccess: invalidate,
    onError: (error) => reportError(error, 'That report could not be updated.'),
  });
}

/** Drops the row and its screenshot folder together. There is no undo. */
export function useDeleteBug() {
  const invalidate = useBugInvalidation();
  return useMutation({
    mutationFn: (id: number) => api.bugs.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success('Report deleted');
    },
    onError: (error) => reportError(error, 'That report could not be deleted.'),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Search
   ──────────────────────────────────────────────────────────────────────────── */

/** Trailing-edge debounce. 140ms is under the perceptual "instant" threshold. */
export function useDebouncedValue<T>(value: T, delay = 140): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export interface SearchOptions {
  delay?: number;
  minLength?: number;
  enabled?: boolean;
}

/**
 * `⌘K` search. Previous results stay on screen while the next ones load, so the
 * palette never collapses to an empty box mid-keystroke.
 */
export function useSearch(term: string, options: SearchOptions = {}) {
  const { delay = 140, minLength = 2, enabled = true } = options;
  const debounced = useDebouncedValue(term, delay);
  const trimmed = debounced.trim();

  return useQuery<SearchResponse>({
    queryKey: queryKeys.search(trimmed),
    queryFn: ({ signal }) => api.search(trimmed, signal),
    enabled: enabled && trimmed.length >= minLength,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Analytics
   ──────────────────────────────────────────────────────────────────────────── */

const ANALYTICS_STALE = 60_000;

/**
 * Seven cards fail together or not at all, and the client's default backoff
 * (1s, 2s, 4s…) spent ten seconds animating a skeleton that was lying before the
 * first of them admitted anything was wrong. Two retries at 400ms and 800ms put
 * the banner on screen inside ~2.5s; the screen shows it on the *first* failure
 * (`failureReason`) with a quiet "Retrying…" line while the rest run.
 */
const ANALYTICS_RETRY = {
  retry: shouldRetry,
  retryDelay: (attempt: number) => Math.min(400 * 2 ** attempt, 1_500),
} as const;

export function useAnalyticsOverview(query: AnalyticsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.overview(query),
    queryFn: ({ signal }) => api.analytics.overview(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

export function useAnalyticsTimeseries(query: TimeseriesQuery = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.timeseries(query),
    queryFn: ({ signal }) => api.analytics.timeseries(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

export function useAnalyticsBreakdown(query: BreakdownQuery) {
  return useQuery({
    queryKey: queryKeys.analytics.breakdown(query),
    queryFn: ({ signal }) => api.analytics.breakdown(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

export function useAnalyticsFunnel(query: AnalyticsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.funnel(query),
    queryFn: ({ signal }) => api.analytics.funnel(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

export function useAnalyticsResponseTimes(query: AnalyticsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.responseTimes(query),
    queryFn: ({ signal }) => api.analytics.responseTimes(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

export function useAnalyticsLeaderboard(query: LeaderboardQuery = {}) {
  return useQuery({
    queryKey: queryKeys.analytics.leaderboard(query),
    queryFn: ({ signal }) => api.analytics.leaderboard(query, signal),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE,
    ...ANALYTICS_RETRY,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   Cache surgery — shared by mutations and by realtime
   ──────────────────────────────────────────────────────────────────────────── */

/** Find a row in any cached list/board, so a detail view can paint immediately. */
export function findInquiryInLists(qc: QueryClient, id: ID): Inquiry | undefined {
  for (const [, data] of qc.getQueriesData<Pages<Inquiry>>({ queryKey: queryKeys.inquiries.lists() })) {
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page.items.find((item) => item.id === id);
      if (hit) return hit;
    }
  }
  for (const [, data] of qc.getQueriesData<BoardResponse>({ queryKey: queryKeys.inquiries.boards() })) {
    if (!data) continue;
    for (const column of data.columns) {
      const hit = column.items.find((item) => item.id === id);
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * The size of the result set the record was opened from — the server's `total`
 * on the cached list page that actually contains this id, not the number of
 * rows that list has scrolled into memory.
 *
 * Without it the detail header prints "2 of 50" beside a toolbar reading
 * "1–50 of 912", and the reader has to guess which 50 it means.
 */
export function findListTotalContaining(qc: QueryClient, id: ID): number | null {
  for (const [, data] of qc.getQueriesData<Pages<Inquiry>>({ queryKey: queryKeys.inquiries.lists() })) {
    if (!data) continue;
    const found = data.pages.some((page) => page.items.some((item) => item.id === id));
    if (!found) continue;
    const total = data.pages[data.pages.length - 1]?.total;
    if (typeof total === 'number') return total;
  }
  return null;
}

function patchLists(qc: QueryClient, id: ID, patch: Partial<Inquiry>): void {
  qc.setQueriesData<Pages<Inquiry>>({ queryKey: queryKeys.inquiries.lists() }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      const index = page.items.findIndex((item) => item.id === id);
      if (index === -1) return page;
      touched = true;
      const items = page.items.slice();
      items[index] = { ...items[index]!, ...patch };
      return { ...page, items };
    });
    return touched ? { ...old, pages } : old;
  });
}

function patchBoards(qc: QueryClient, id: ID, patch: Partial<Inquiry>): void {
  qc.setQueriesData<BoardResponse>({ queryKey: queryKeys.inquiries.boards() }, (old) => {
    if (!old) return old;

    let source: Inquiry | undefined;
    let sourceStage: string | undefined;
    for (const column of old.columns) {
      const hit = column.items.find((item) => item.id === id);
      if (hit) {
        source = hit;
        sourceStage = column.stage.key;
        break;
      }
    }
    if (!source || sourceStage === undefined) return old;

    const next = { ...source, ...patch };
    const targetStage = patch.stageKey ?? sourceStage;

    if (targetStage === sourceStage) {
      return {
        ...old,
        columns: old.columns.map((column) =>
          column.stage.key === sourceStage
            ? {
                ...column,
                items: column.items.map((item) => (item.id === id ? next : item)),
              }
            : column,
        ),
      };
    }

    return {
      ...old,
      columns: old.columns.map((column) => {
        if (column.stage.key === sourceStage) {
          return {
            ...column,
            items: column.items.filter((item) => item.id !== id),
            total: Math.max(0, column.total - 1),
          };
        }
        if (column.stage.key === targetStage) {
          return { ...column, items: [next, ...column.items], total: column.total + 1 };
        }
        return column;
      }),
    };
  });
}

/** Apply a partial to the detail cache, every list page and every board column. */
export function patchInquiryEverywhere(qc: QueryClient, id: ID, patch: Partial<Inquiry>): void {
  qc.setQueryData<Inquiry>(queryKeys.inquiries.detail(id), (old) =>
    old ? { ...old, ...patch } : old,
  );
  patchLists(qc, id, patch);
  patchBoards(qc, id, patch);
}

/** Insert a brand-new record at the top of a list (realtime `inquiry.created`). */
export function prependInquiry(qc: QueryClient, key: QueryKey, inquiry: Inquiry): void {
  qc.setQueryData<Pages<Inquiry>>(key, (old) => {
    if (!old || old.pages.length === 0) return old;
    const [first, ...rest] = old.pages;
    if (!first || first.items.some((item) => item.id === inquiry.id)) return old;
    return {
      ...old,
      pages: [{ ...first, items: [inquiry, ...first.items], total: first.total + 1 }, ...rest],
    };
  });
}

export function removeInquiryEverywhere(qc: QueryClient, id: ID): void {
  qc.removeQueries({ queryKey: queryKeys.inquiries.detail(id) });

  qc.setQueriesData<Pages<Inquiry>>({ queryKey: queryKeys.inquiries.lists() }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      if (!page.items.some((item) => item.id === id)) return page;
      touched = true;
      return {
        ...page,
        items: page.items.filter((item) => item.id !== id),
        total: Math.max(0, page.total - 1),
      };
    });
    return touched ? { ...old, pages } : old;
  });

  qc.setQueriesData<BoardResponse>({ queryKey: queryKeys.inquiries.boards() }, (old) => {
    if (!old) return old;
    let touched = false;
    const columns = old.columns.map((column) => {
      if (!column.items.some((item) => item.id === id)) return column;
      touched = true;
      return {
        ...column,
        items: column.items.filter((item) => item.id !== id),
        total: Math.max(0, column.total - 1),
      };
    });
    return touched ? { ...old, columns, total: Math.max(0, old.total - 1) } : old;
  });
}

/** Everything the optimistic pass touched, so a failure can put it all back. */
type Snapshot = Array<[QueryKey, unknown]>;

function snapshotInquiries(qc: QueryClient): Snapshot {
  return qc.getQueriesData({ queryKey: queryKeys.inquiries.all() });
}

function restore(qc: QueryClient, snapshot: Snapshot | undefined): void {
  if (!snapshot) return;
  for (const [key, data] of snapshot) qc.setQueryData(key, data);
}

/** The subset of a patch that maps straight onto an `Inquiry` field. */
function patchToOptimistic(patch: InquiryPatch): Partial<Inquiry> {
  const next: Partial<Inquiry> = {};
  if (patch.stageKey !== undefined) next.stageKey = patch.stageKey;
  if (patch.status !== undefined) next.status = patch.status;
  if (patch.priority !== undefined) next.priority = patch.priority;
  if (patch.ownerId !== undefined) next.ownerId = patch.ownerId;
  if (patch.region !== undefined) next.region = patch.region;
  if (patch.type !== undefined) next.type = patch.type;
  if (patch.subject !== undefined) next.subject = patch.subject;
  if (patch.contactId !== undefined) next.contactId = patch.contactId;
  if (patch.companyId !== undefined) next.companyId = patch.companyId;
  if (patch.slaDueAt !== undefined) next.slaDueAt = patch.slaDueAt;
  if (patch.value !== undefined) next.value = patch.value;
  return next;
}

/** Extra display fields the caller can supply (the `User` behind an `ownerId`). */
export type OptimisticInput = Partial<Inquiry> | ((current: Inquiry) => Partial<Inquiry>);

function resolveOptimistic(
  current: Inquiry | undefined,
  input: OptimisticInput | undefined,
): Partial<Inquiry> {
  if (!input) return {};
  if (typeof input === 'function') return current ? input(current) : {};
  return input;
}

/* ────────────────────────────────────────────────────────────────────────────
   Inquiries — mutations
   ──────────────────────────────────────────────────────────────────────────── */

export interface PatchInquiryVars {
  id: ID;
  patch: InquiryPatch;
  /**
   * Display fields the patch cannot imply — pass the `User` when assigning, the
   * `Tag[]` when tagging, so the row repaints fully instead of half-blank.
   */
  optimistic?: OptimisticInput;
}

export function usePatchInquiry() {
  const qc = useQueryClient();

  return useMutation<Inquiry, unknown, PatchInquiryVars, { snapshot: Snapshot }>({
    mutationFn: ({ id, patch }) => api.inquiries.patch(id, patch),

    onMutate: async ({ id, patch, optimistic }) => {
      await qc.cancelQueries({ queryKey: queryKeys.inquiries.all() });
      const snapshot = snapshotInquiries(qc);

      const current = qc.getQueryData<Inquiry>(queryKeys.inquiries.detail(id)) ?? findInquiryInLists(qc, id);
      patchInquiryEverywhere(qc, id, {
        ...patchToOptimistic(patch),
        ...resolveOptimistic(current, optimistic),
        updatedAt: new Date().toISOString(),
      });

      return { snapshot };
    },

    onError: (error, _vars, context) => {
      restore(qc, context?.snapshot);
      reportError(error, 'Could not save that change.');
    },

    onSuccess: (inquiry) => {
      patchInquiryEverywhere(qc, inquiry.id, inquiry);
    },

    onSettled: (_data, _error, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.detail(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.lists() });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.boards() });
    },
  });
}

export interface BulkVars {
  request: BulkRequest;
  optimistic?: OptimisticInput;
}

/** Maps a bulk op onto the row-level patch it implies. */
function bulkToPatch(op: BulkRequest['op']): Partial<Inquiry> {
  switch (op.action) {
    case 'assign':
      // The `User` behind an id is unknown here — callers pass it via `optimistic`.
      return op.ownerId === null ? { ownerId: null, owner: null } : { ownerId: op.ownerId };
    case 'stage':
      return { stageKey: op.stageKey };
    case 'status':
      return { status: op.status };
    case 'priority':
      return { priority: op.priority };
    default:
      return {};
  }
}

export function useBulkInquiries() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ request }: BulkVars) => api.inquiries.bulk(request),

    onMutate: async ({ request, optimistic }: BulkVars) => {
      await qc.cancelQueries({ queryKey: queryKeys.inquiries.all() });
      const snapshot = snapshotInquiries(qc);

      if (request.op.action === 'delete') {
        for (const id of request.ids) removeInquiryEverywhere(qc, id);
      } else {
        const base = bulkToPatch(request.op);
        const updatedAt = new Date().toISOString();
        for (const id of request.ids) {
          const current =
            qc.getQueryData<Inquiry>(queryKeys.inquiries.detail(id)) ?? findInquiryInLists(qc, id);
          patchInquiryEverywhere(qc, id, {
            ...base,
            ...resolveOptimistic(current, optimistic),
            updatedAt,
          });
        }
      }
      return { snapshot };
    },

    onError: (error, _vars, context) => {
      restore(qc, context?.snapshot);
      reportError(error, 'The bulk update did not go through.');
    },

    onSuccess: (result, { request }) => {
      if (result.failed.length > 0) {
        toast.error(`${result.failed.length} of ${request.ids.length} could not be updated`, {
          description: result.failed[0]?.reason,
        });
      }
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.all() });
    },
  });
}

/* ── Notes ───────────────────────────────────────────────────────────────── */

export interface CreateNoteVars extends NoteInput {
  inquiryId: ID;
}

function tempId(prefix: string): string {
  return `${prefix}_tmp_${Math.random().toString(36).slice(2, 10)}`;
}

export function useCreateNote() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, ...input }: CreateNoteVars) => api.inquiries.createNote(inquiryId, input),

    onMutate: async ({ inquiryId, body, isPinned = false }: CreateNoteVars) => {
      const key = queryKeys.inquiries.notes(inquiryId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Paginated<Note>>(key);
      const session = qc.getQueryData<SessionResponse>(queryKeys.session());

      const now = new Date().toISOString();
      const optimisticNote: Note = {
        id: tempId('note'),
        inquiryId,
        authorId: session?.user.id ?? '',
        author: session?.user,
        body,
        isPinned,
        createdAt: now,
        updatedAt: now,
      };

      qc.setQueryData<Paginated<Note>>(key, (old) =>
        old
          ? { ...old, items: [optimisticNote, ...old.items], total: old.total + 1 }
          : { items: [optimisticNote], nextCursor: null, total: 1 },
      );

      patchInquiryEverywhere(qc, inquiryId, { lastActivityAt: now });
      bumpCounts(qc, inquiryId, { notes: 1 });

      return { previous, key };
    },

    onError: (error, _vars, context) => {
      if (context?.key) qc.setQueryData(context.key, context.previous);
      reportError(error, 'Your note was not saved.');
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.notes(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
    },
  });
}

function bumpCounts(qc: QueryClient, inquiryId: ID, delta: Partial<Record<keyof NonNullable<Inquiry['counts']>, number>>): void {
  qc.setQueryData<Inquiry>(queryKeys.inquiries.detail(inquiryId), (old) => {
    if (!old?.counts) return old;
    const counts = { ...old.counts };
    for (const [key, value] of Object.entries(delta)) {
      const field = key as keyof NonNullable<Inquiry['counts']>;
      counts[field] = Math.max(0, counts[field] + (value ?? 0));
    }
    return { ...old, counts };
  });
}

/* ── Attachments ─────────────────────────────────────────────────────────── */

export interface UploadAttachmentsVars {
  inquiryId: ID;
  files: File[];
}

/**
 * Not optimistic: the server assigns the id, the content hash and the download
 * URL, and the only honest optimistic row would be one the user cannot open.
 * The button carries the pending state instead, and the record refetches with
 * the real rows.
 */
export function useUploadAttachments(): UseMutationResult<
  Attachment[],
  unknown,
  UploadAttachmentsVars
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, files }: UploadAttachmentsVars) =>
      api.inquiries.uploadAttachments(inquiryId, files),

    onSuccess: (attachments, { inquiryId }) => {
      qc.setQueryData<Inquiry>(queryKeys.inquiries.detail(inquiryId), (old) =>
        old ? { ...old, attachments: [...attachments, ...(old.attachments ?? [])] } : old,
      );
      bumpCounts(qc, inquiryId, { attachments: attachments.length });
      toast.success(
        attachments.length === 1
          ? `${attachments[0].filename} attached`
          : `${attachments.length} files attached`,
      );
    },

    onError: (error) => reportError(error, 'The upload did not go through.'),

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.detail(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
    },
  });
}

/* ── Tasks ───────────────────────────────────────────────────────────────── */

export interface CreateTaskVars extends TaskInput {
  inquiryId: ID;
}

export function useCreateTask() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, ...input }: CreateTaskVars) => api.inquiries.createTask(inquiryId, input),

    onMutate: async ({ inquiryId, title, assigneeId = null, dueAt = null, priority = 'normal' }: CreateTaskVars) => {
      const key = queryKeys.inquiries.tasks(inquiryId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Paginated<Task>>(key);
      const session = qc.getQueryData<SessionResponse>(queryKeys.session());
      const users = qc.getQueryData<User[]>(queryKeys.users());

      const optimisticTask: Task = {
        id: tempId('task'),
        inquiryId,
        title,
        assigneeId,
        assignee: assigneeId ? (users?.find((u) => u.id === assigneeId) ?? null) : null,
        dueAt,
        completedAt: null,
        priority,
        createdById: session?.user.id ?? '',
        createdAt: new Date().toISOString(),
      };

      qc.setQueryData<Paginated<Task>>(key, (old) =>
        old
          ? { ...old, items: [optimisticTask, ...old.items], total: old.total + 1 }
          : { items: [optimisticTask], nextCursor: null, total: 1 },
      );
      bumpCounts(qc, inquiryId, { tasks: 1, openTasks: 1 });

      return { previous, key };
    },

    onError: (error, _vars, context) => {
      if (context?.key) qc.setQueryData(context.key, context.previous);
      reportError(error, 'The task was not created.');
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.tasks(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
    },
  });
}

export interface ToggleTaskVars {
  id: ID;
  inquiryId: ID;
  complete: boolean;
}

export function useToggleTask() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, complete }: ToggleTaskVars) => api.tasks.setComplete(id, complete),

    onMutate: async ({ id, inquiryId, complete }: ToggleTaskVars) => {
      await qc.cancelQueries({ queryKey: queryKeys.tasks.all() });
      await qc.cancelQueries({ queryKey: queryKeys.inquiries.tasks(inquiryId) });

      const snapshot: Snapshot = [
        ...qc.getQueriesData({ queryKey: queryKeys.tasks.all() }),
        ...qc.getQueriesData({ queryKey: queryKeys.inquiries.tasks(inquiryId) }),
      ];
      const completedAt = complete ? new Date().toISOString() : null;

      qc.setQueryData<Paginated<Task>>(queryKeys.inquiries.tasks(inquiryId), (old) =>
        old
          ? { ...old, items: old.items.map((t) => (t.id === id ? { ...t, completedAt } : t)) }
          : old,
      );

      qc.setQueriesData<Pages<Task>>({ queryKey: queryKeys.tasks.lists() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((t) => (t.id === id ? { ...t, completedAt } : t)),
          })),
        };
      });

      bumpCounts(qc, inquiryId, { openTasks: complete ? -1 : 1 });

      return { snapshot };
    },

    onError: (error, _vars, context) => {
      restore(qc, context?.snapshot);
      reportError(error, 'The task did not update.');
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.tasks.all() });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.tasks(inquiryId) });
    },
  });
}

/* ── Tags ────────────────────────────────────────────────────────────────── */

export function useCreateTag() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof api.tags.create>[0]) => api.tags.create(input),

    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.tags() });
      const previous = qc.getQueryData<Tag[]>(queryKeys.tags());
      const optimisticTag: Tag = { id: tempId('tag'), name: input.name, color: input.color, count: 0 };
      qc.setQueryData<Tag[]>(queryKeys.tags(), (old) => [...(old ?? []), optimisticTag]);
      return { previous };
    },

    onError: (error, _vars, context) => {
      qc.setQueryData(queryKeys.tags(), context?.previous);
      reportError(error, 'The tag was not created.');
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.tags() });
    },
  });
}

/* ── Saved views ─────────────────────────────────────────────────────────── */

export interface SaveViewVars {
  /** Omit to create. */
  id?: ID;
  input: SavedViewInput | Partial<SavedViewInput>;
}

export function useSaveView() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: SaveViewVars) =>
      id ? api.views.patch(id, input) : api.views.create(input as SavedViewInput),

    onMutate: async ({ id, input }: SaveViewVars) => {
      await qc.cancelQueries({ queryKey: queryKeys.views() });
      const previous = qc.getQueryData<SavedView[]>(queryKeys.views());
      const session = qc.getQueryData<SessionResponse>(queryKeys.session());

      qc.setQueryData<SavedView[]>(queryKeys.views(), (old) => {
        const list = old ?? [];
        if (id) return list.map((v) => (v.id === id ? { ...v, ...input } : v));
        const draft: SavedView = {
          id: tempId('view'),
          name: input.name ?? 'Untitled view',
          entity: input.entity ?? 'inquiries',
          filters: input.filters ?? { combinator: 'and', conditions: [] },
          sort: input.sort ?? [],
          columns: input.columns ?? [],
          layout: input.layout ?? 'table',
          ownerId: session?.user.id ?? '',
          isShared: input.isShared ?? false,
          isDefault: input.isDefault ?? false,
          icon: input.icon ?? null,
          position: input.position ?? list.length,
          createdAt: new Date().toISOString(),
        };
        return [...list, draft];
      });

      return { previous };
    },

    onError: (error, _vars, context) => {
      qc.setQueryData(queryKeys.views(), context?.previous);
      reportError(error, 'The view was not saved.');
    },

    onSuccess: (view, { id }) => {
      toast.success(id ? `View “${view.name}” updated` : `View “${view.name}” saved`);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.views() });
    },
  });
}

export function useDeleteView() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: ID) => api.views.remove(id),

    onMutate: async (id: ID) => {
      await qc.cancelQueries({ queryKey: queryKeys.views() });
      const previous = qc.getQueryData<SavedView[]>(queryKeys.views());
      qc.setQueryData<SavedView[]>(queryKeys.views(), (old) => (old ?? []).filter((v) => v.id !== id));
      return { previous };
    },

    onError: (error, _vars, context) => {
      qc.setQueryData(queryKeys.views(), context?.previous);
      reportError(error, 'The view was not deleted.');
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.views() });
    },
  });
}

/* ── Activities ──────────────────────────────────────────────────────────── */

/** Realtime and the note/task mutations both feed the same feed cache. */
export function prependActivity(qc: QueryClient, activity: Activity): void {
  qc.setQueryData<Paginated<Activity>>(queryKeys.inquiries.activities(activity.inquiryId), (old) => {
    if (!old) return old;
    if (old.items.some((item) => item.id === activity.id)) return old;
    return { ...old, items: [activity, ...old.items], total: old.total + 1 };
  });
}

export function prependNote(qc: QueryClient, note: Note): void {
  qc.setQueryData<Paginated<Note>>(queryKeys.inquiries.notes(note.inquiryId), (old) => {
    if (!old) return old;
    if (old.items.some((item) => item.id === note.id)) return old;
    return { ...old, items: [note, ...old.items], total: old.total + 1 };
  });
}

export function upsertTask(qc: QueryClient, task: Task): void {
  qc.setQueryData<Paginated<Task>>(queryKeys.inquiries.tasks(task.inquiryId), (old) => {
    if (!old) return old;
    const exists = old.items.some((item) => item.id === task.id);
    return {
      ...old,
      items: exists ? old.items.map((item) => (item.id === task.id ? task : item)) : [task, ...old.items],
      total: exists ? old.total : old.total + 1,
    };
  });

  qc.setQueriesData<Pages<Task>>({ queryKey: queryKeys.tasks.lists() }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((page) => {
      if (!page.items.some((item) => item.id === task.id)) return page;
      touched = true;
      return { ...page, items: page.items.map((item) => (item.id === task.id ? task : item)) };
    });
    return touched ? { ...old, pages } : old;
  });
}

/* ── Messages (the reply thread) ───────────────────────────────────────────────
   The composer is a thin view over this: `useMessages` for the thread,
   `useSendMessage` for the send. Everything below is deliberately shaped so a
   screen never needs to know a cache key or a rollback rule.

   Keys sit under `inquiries.detail(id)` on purpose — an inquiry invalidation
   already sweeps the thread with it, and nothing else has to be taught about
   messages.                                                                   */

export const messageKeys = {
  all: (inquiryId: ID) => [ROOT, 'inquiries', 'detail', inquiryId, 'messages'] as const,
} as const;

export function useMessages(inquiryId: ID | null | undefined) {
  return useQuery({
    queryKey: messageKeys.all(inquiryId ?? 'none'),
    queryFn: ({ signal }) => api.messages.list(inquiryId as ID, undefined, signal),
    enabled: Boolean(inquiryId),
    staleTime: 15_000,
  });
}

export interface SendMessageVars extends MessageInput {
  inquiryId: ID;
}

/**
 * Append the message the instant the user commits, then reconcile.
 *
 * The optimistic row is honest about what it does not know: its id is a
 * placeholder, and its status is `queued` — which is also what the server will
 * write, because there is no mail transport, so the optimistic state and the
 * confirmed state agree instead of the row flipping under the user. On failure
 * the whole thread snapshot goes back and a toast explains, so a reply is never
 * silently lost.
 */
export function useSendMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, ...input }: SendMessageVars) => api.messages.create(inquiryId, input),

    onMutate: async (vars: SendMessageVars) => {
      const key = messageKeys.all(vars.inquiryId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Paginated<Message>>(key);
      const session = qc.getQueryData<SessionResponse>(queryKeys.session());

      const now = new Date().toISOString();
      const direction = vars.direction ?? 'outbound';
      const channel = vars.channel ?? 'email';
      const optimistic: Message = {
        id: tempId('msg'),
        inquiryId: vars.inquiryId,
        contactId: null,
        userId: session?.user.id ?? null,
        author: session?.user ?? null,
        channel,
        direction,
        status: direction === 'inbound' ? 'received' : channel === 'email' ? 'queued' : 'logged',
        subject: vars.subject ?? null,
        body: vars.body,
        fromAddress: session?.user.email ?? null,
        toAddress: vars.to ?? null,
        ccAddress: vars.cc ?? null,
        durationSeconds: vars.durationSeconds ?? null,
        externalId: null,
        occurredAt: vars.occurredAt ?? now,
        createdAt: now,
        editableUntil: null,
      };

      // Oldest first: the newest message belongs at the bottom of the thread.
      qc.setQueryData<Paginated<Message>>(key, (old) =>
        old
          ? { ...old, items: [...old.items, optimistic], total: old.total + 1 }
          : { items: [optimistic], nextCursor: null, total: 1 },
      );

      patchInquiryEverywhere(qc, vars.inquiryId, { lastActivityAt: now });

      return { previous, key, optimisticId: optimistic.id };
    },

    onError: (error, _vars, context) => {
      if (context?.key) qc.setQueryData(context.key, context.previous);
      reportError(error, 'Your reply was not saved. Nothing has been sent.');
    },

    onSuccess: (result, vars, context) => {
      // Swap the placeholder for the server's row in place, so the thread does
      // not jump between the optimistic render and the refetch.
      qc.setQueryData<Paginated<Message>>(messageKeys.all(vars.inquiryId), (old) => {
        if (!old) return old;
        const placeholder = context?.optimisticId;
        const items = old.items.some((item) => item.id === placeholder)
          ? old.items.map((item) => (item.id === placeholder ? result.message : item))
          : old.items.some((item) => item.id === result.message.id)
            ? old.items
            : [...old.items, result.message];
        return { ...old, items };
      });

      prependActivity(qc, result.activity);
      // The send stopped the SLA clock (and may have moved the score); the
      // header, the list row and the board card all read those fields.
      if (result.inquiry) patchInquiryEverywhere(qc, vars.inquiryId, result.inquiry);

      /*
       * The confirmation has to exist independently of the thread.
       * The new row lands at the bottom of a scroller that may be showing an
       * older part of the history, so "it worked" cannot be left to a node the
       * reader might not be looking at. `Queued`, not `Sent` — nothing is
       * delivered, and this toast is the one place a rep would most easily
       * misread that.
       */
      const address = result.message.toAddress ?? vars.to;
      toast.success(address ? `Reply queued to ${address}` : 'Reply queued');
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: messageKeys.all(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.detail(inquiryId) });
      // First-response time feeds every response-time metric.
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.all() });
    },
  });
}

export interface UpdateMessageVars extends MessagePatch {
  id: ID;
  inquiryId: ID;
}

/** Correct a subject or body inside the server's edit window. */
export function useUpdateMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, inquiryId: _inquiryId, ...patch }: UpdateMessageVars) =>
      api.messages.update(id, patch),

    onMutate: async ({ id, inquiryId, ...patch }: UpdateMessageVars) => {
      const key = messageKeys.all(inquiryId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Paginated<Message>>(key);

      qc.setQueryData<Paginated<Message>>(key, (old) =>
        old
          ? {
              ...old,
              items: old.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
            }
          : old,
      );

      return { previous, key };
    },

    onError: (error, _vars, context) => {
      if (context?.key) qc.setQueryData(context.key, context.previous);
      reportError(error, 'That edit was not saved.');
    },

    onSuccess: (message, { inquiryId }) => {
      qc.setQueryData<Paginated<Message>>(messageKeys.all(inquiryId), (old) =>
        old
          ? { ...old, items: old.items.map((item) => (item.id === message.id ? message : item)) }
          : old,
      );
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: messageKeys.all(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
    },
  });
}

export interface DeleteMessageVars {
  id: ID;
  inquiryId: ID;
}

/** Retract, inside the same window. The SLA stamp is not rewound server-side. */
export function useDeleteMessage() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: DeleteMessageVars) => api.messages.remove(id),

    onMutate: async ({ id, inquiryId }: DeleteMessageVars) => {
      const key = messageKeys.all(inquiryId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Paginated<Message>>(key);

      qc.setQueryData<Paginated<Message>>(key, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((item) => item.id !== id),
              total: Math.max(0, old.total - 1),
            }
          : old,
      );

      return { previous, key };
    },

    onError: (error, _vars, context) => {
      if (context?.key) qc.setQueryData(context.key, context.previous);
      reportError(error, 'That message could not be retracted.');
    },

    onSettled: (_data, _error, { inquiryId }) => {
      void qc.invalidateQueries({ queryKey: messageKeys.all(inquiryId) });
      void qc.invalidateQueries({ queryKey: queryKeys.inquiries.activities(inquiryId) });
    },
  });
}

/** Realtime / cross-hook entry point, mirroring `prependNote`. */
export function appendMessage(qc: QueryClient, message: Message): void {
  qc.setQueryData<Paginated<Message>>(messageKeys.all(message.inquiryId), (old) => {
    if (!old) return old;
    if (old.items.some((item) => item.id === message.id)) return old;
    return { ...old, items: [...old.items, message], total: old.total + 1 };
  });
}
